import { CaretRightOutlined } from "@ant-design/icons";
import { Checkbox, Select, Collapse, Slider } from "antd";
import { RampTheme, RAMPS, themeColors } from "src/utils/color";
import { useColorScheme } from "src/hooks/useColorScheme";

export type ColorRampConfig = {
  theme: RampTheme;
  isMonotone: boolean;
  isMuted: boolean;
  isPastel: boolean;
  hueReverse: boolean;
  hueShift: number;
};

function ThemePreview({
  name,
  description,
  colors,
  showDescription = true,
}: {
  name: string;
  description: string;
  colors: string[];
  showDescription?: boolean;
}) {
  return (
    <div
      style={{
        padding: "0",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontWeight: 500 }}>{name}</div>
        <div style={{ flex: 1, marginLeft: 8 }}>
          <div
            style={{
              height: 16,
              borderRadius: 2,
              background: `linear-gradient(to right, ${colors.join(", ")})`,
            }}
          />
        </div>
      </div>
      {showDescription && (
        <div
          style={{
            fontSize: "12px",
            color: "var(--color-text-tertiary)",
            marginTop: 4,
          }}
        >
          {description}
        </div>
      )}
    </div>
  );
}

export function ColorRampPicker({
  disableMonotone,
  hueReverse,
  hueShift,
  monotone,
  muted,
  pastel,
  ramp,
  onChangeHueReverse,
  onChangeHueShift,
  onChangeMonotone,
  onChangeMuted,
  onChangePastel,
  onChangeRamp,
}: {
  disableMonotone?: boolean;
  hueReverse: boolean;
  hueShift: number;
  monotone: boolean;
  muted: boolean;
  pastel: boolean;
  ramp: RampTheme;
  onChangeHueReverse(reverse: boolean): void;
  onChangeHueShift(shift: number): void;
  onChangeMonotone(value: boolean): void;
  onChangeMuted(value: boolean): void;
  onChangePastel(value: boolean): void;
  onChangeRamp(value: RampTheme): void;
}) {
  const mode = useColorScheme();

  const currentColors = themeColors(
    ramp,
    mode,
    monotone,
    muted,
    pastel,
    hueReverse,
    hueShift,
    12
  );

  return (
    <div style={{ width: "100%" }}>
      {/* Theme Selector */}
      <div style={{ marginBottom: 12 }}>
        <Select
          value={ramp}
          onChange={onChangeRamp}
          style={{ width: "100%" }}
          bordered={false}
          popupMatchSelectWidth={false}
          className="viz-select"
          labelInValue={false}
        >
          {Object.entries(RAMPS).map(([rampId, rampInfo]) => {
            const previewColors = themeColors(
              rampId as RampTheme,
              mode,
              false,
              false,
              false,
              false,
              0,
              12
            );
            return (
              <Select.Option
                key={rampId}
                value={rampId}
                className="viz-select-option"
              >
                <ThemePreview
                  name={rampInfo.name}
                  description={rampInfo.description}
                  colors={previewColors}
                />
              </Select.Option>
            );
          })}
        </Select>
      </div>

      {/* Advanced Section */}
      <div style={{ marginTop: 0 }}>
        <Collapse
          className="advanced-collapse"
          style={{ padding: "0" }}
          ghost
          expandIcon={({ isActive }) => (
            <CaretRightOutlined
              rotate={isActive ? 90 : 0}
              style={{
                fontSize: 10,
                color: "var(--color-text-tertiary)",
                paddingLeft: 0,
              }}
            />
          )}
        >
          <Collapse.Panel
            header={
              <span
                style={{ color: "var(--color-text-tertiary)", fontSize: 13 }}
              >
                Advanced Color Settings
              </span>
            }
            key="1"
            style={{ padding: 0, border: "none" }}
          >
            <div style={{ padding: "4px 0 8px 12px" }}>
              {/* Color Adjustments */}
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--color-text-tertiary)",
                    marginBottom: 12,
                    fontWeight: 500,
                  }}
                >
                  Color adjustments
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {!disableMonotone && (
                    <Checkbox
                      checked={monotone}
                      onChange={(e) => onChangeMonotone(e.target.checked)}
                    >
                      Discrete Color Range
                    </Checkbox>
                  )}
                  <Checkbox
                    checked={muted}
                    onChange={(e) => onChangeMuted(e.target.checked)}
                  >
                    Soften Colors
                  </Checkbox>
                  <Checkbox
                    checked={hueReverse}
                    onChange={(e) => onChangeHueReverse(e.target.checked)}
                  >
                    Reverse Range
                  </Checkbox>
                </div>
              </div>

              {/* Range Adjustments */}
              <div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--color-text-tertiary)",
                    marginBottom: 12,
                    fontWeight: 500,
                  }}
                >
                  Range Adjustments
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        color: "var(--color-text-tertiary)",
                      }}
                    >
                      Hue Shift
                    </span>
                    <div
                      onMouseUp={() => {
                        // Force blur on mouse up to ensure slider stops
                        const activeElement =
                          document.activeElement as HTMLElement;
                        if (activeElement) {
                          activeElement.blur();
                        }
                      }}
                    >
                      <Slider
                        value={hueShift}
                        onChange={onChangeHueShift}
                        min={0}
                        max={360}
                        step={1}
                        tooltip={{
                          formatter: (value) => `${value}°`,
                        }}
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        height: 16,
                        borderRadius: 2,
                        background: `linear-gradient(to right, ${currentColors.join(
                          ", "
                        )})`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Collapse.Panel>
        </Collapse>
      </div>
    </div>
  );
}

export function toColorRampConfig(
  data: ColorRampConfig | any
): ColorRampConfig {
  return {
    isMonotone: typeof data?.isMonotone === "boolean" ? data.isMonotone : false,
    isMuted: typeof data?.isMuted === "boolean" ? data.isMuted : false,
    isPastel: typeof data?.isPastel === "boolean" ? data.isPastel : false,
    hueReverse: typeof data?.hueReverse === "boolean" ? data.hueReverse : false,
    hueShift: typeof data?.hueShift === "number" ? data.hueShift : 0,
    theme: Object.values(RampTheme).includes(data?.theme)
      ? data.theme
      : RampTheme.classic,
  };
}
